import {createSelector} from 'reselect'
import isEmpty from 'lodash/isEmpty.js'
import get from 'lodash/get.js'

import api from '../../../../common/api.js'
import {
  productSelector,
  productWarehouseSelector,
  getProduct,
} from '../../../../data/products.js'
import {setForm, updateForm, removeForm, getState} from '../../../../store.js'
import {showMessageToast} from '../../../Header/Toast/index.js'
import {isNumeric} from '../../../../common/utils.js'
import {formsSelector} from '../../../../redux/selectors/ui/forms.js'
import {refreshTransferList} from '../../transferListFunctions.js'

export const NO_WAREHOUSE_SELECTED = 'NO_WAREHOUSE_SELECTED'

export const MODAL_FORM = 'create_inventory_transfer_modal'

export function showCreateInventoryTransferModal(skus) {
  setForm(MODAL_FORM, {
    skus: skus ? skus : [],
    isSaving: false,
    serverError: null,
    sending_warehouse_id: NO_WAREHOUSE_SELECTED,
    receiving_warehouse_id: NO_WAREHOUSE_SELECTED,
    afterAddingProduct: false,
  })
}

export function updateCreateInventoryTransferModalForm(updates) {
  updateForm(MODAL_FORM, updates)
}

export function formatCreateInventoryTransferModalRowFormID(sku) {
  return `${MODAL_FORM}_row_${sku}`
}

export function closeCreateInventoryTransferModal() {
  removeForm(MODAL_FORM)
}

export const createInventoryTransferModalFormSelector = createSelector(
  formsSelector,
  (forms) => forms[MODAL_FORM],
)

export function createInventoryTransferModalRowFormSelector(state, {sku}) {
  const forms = formsSelector(state)

  return forms[formatCreateInventoryTransferModalRowFormID(sku)]
}

export function rowsSelector(state) {
  const form = createInventoryTransferModalFormSelector(state)

  if (!form) {
    return []
  }

  return form.skus.map((sku) =>
    createInventoryTransferModalRowFormSelector(state, {sku}),
  )
}

function getRowErrors(state, row, sku) {
  if (!row) {
    return {}
  }

  const errors = {}
  const product = productSelector(state, {sku})

  if (!isNumeric(row.quantity) || row.quantity <= 0) {
    errors.quantity = 'Enter a positive number'
  }

  if (product && product.archive_date !== null) {
    errors.product = 'This is an archived product'
  }

  return errors
}

function getRowWarnings(state, row, sku) {
  if (!row) {
    return {}
  }

  const warnings = {}
  const sending_warehouse_id = sendingWarehouseIDSelector(state)

  const productWarehouseInventory = productWarehouseSelector(state, {
    sku,
    warehouseID: sending_warehouse_id,
  })

  if (
    isNumeric(row.quantity) &&
    productWarehouseInventory &&
    row.quantity > productWarehouseInventory.available
  ) {
    warnings.quantity = 'This will result in negative warehouse inventory'
  }

  return warnings
}

export const sendingWarehouseIDSelector = createSelector(
  createInventoryTransferModalFormSelector,
  (form) => get(form, 'sending_warehouse_id', NO_WAREHOUSE_SELECTED),
)

export const receivingWarehouseIDSelector = createSelector(
  createInventoryTransferModalFormSelector,
  (form) => get(form, 'receiving_warehouse_id', NO_WAREHOUSE_SELECTED),
)

export const warehouseSelectionIsValidSelector = createSelector(
  sendingWarehouseIDSelector,
  receivingWarehouseIDSelector,
  (sending_warehouse_id, receiving_warehouse_id) => {
    return (
      sending_warehouse_id !== NO_WAREHOUSE_SELECTED &&
      receiving_warehouse_id !== NO_WAREHOUSE_SELECTED &&
      sending_warehouse_id !== receiving_warehouse_id
    )
  },
)

export function errorsSelector(state, {sku}) {
  const row = createInventoryTransferModalRowFormSelector(state, {sku})

  return getRowErrors(state, row, sku)
}

export function warningsSelector(state, {sku}) {
  const row = createInventoryTransferModalRowFormSelector(state, {sku})

  return getRowWarnings(state, row, sku)
}

export function allRowErrorsSelector(state) {
  const form = createInventoryTransferModalFormSelector(state)

  if (!form) {
    return null
  }

  return form.skus.map((sku) => errorsSelector(state, {sku}))
}

export const hasErrorsSelector = createSelector(
  warehouseSelectionIsValidSelector,
  rowsSelector,
  allRowErrorsSelector,
  (warehouseSelectionIsValid, rows, allRowErrors) =>
    !warehouseSelectionIsValid ||
    rows.length < 1 ||
    allRowErrors.reduce(
      (prev, rowErrors) => prev || !isEmpty(rowErrors),
      false,
    ),
)

export function inventoryTransferParamsSelector(state) {
  const form = createInventoryTransferModalFormSelector(state)

  const params = {
    sending_warehouse_id: get(form, 'sending_warehouse_id'),
    receiving_warehouse_id: get(form, 'receiving_warehouse_id'),
    transfer_notes: get(form, 'transfer_notes'),
    lines: form.skus.map((sku) => {
      const row = createInventoryTransferModalRowFormSelector(state, {sku})

      return {
        sku,
        quantity: Number(get(row, 'quantity')),
        line_notes: get(row, 'line_notes'),
      }
    }),
  }

  return params
}

export function setCreateInventoryTransferModalRowForm(sku, form) {
  const formID = formatCreateInventoryTransferModalRowFormID(sku)

  return setForm(formID, form)
}

export function updateCreateInventoryTransferModalRowForm(sku, updates) {
  const formID = formatCreateInventoryTransferModalRowFormID(sku)

  return updateForm(formID, updates)
}

export function removeCreateInventoryTransferModalRowForm(sku) {
  const formID = formatCreateInventoryTransferModalRowFormID(sku)

  return removeForm(formID)
}

export async function createInventoryTransfer() {
  try {
    updateCreateInventoryTransferModalForm({
      isSaving: true,
      serverError: null,
    })

    const params = inventoryTransferParamsSelector(getState())

    await api.post('/inventory_transfer/', params)

    const promises = []
    for (const line of params.lines) {
      promises.push(getProduct(line.sku))
    }

    await Promise.all(promises)

    showMessageToast('Inventory has been transferred!')

    refreshTransferList()

    closeCreateInventoryTransferModal()
  } catch (err) {
    updateCreateInventoryTransferModalForm({
      serverError: `Error creating inventory transfer: ${
        err.error_message || err.message
      }`,
    })
  } finally {
    updateCreateInventoryTransferModalForm({
      isSaving: false,
    })
  }
}

export async function getProductForInventoryTransferCreate(sku) {
  try {
    await getProduct(sku)
  } catch (err) {
    updateCreateInventoryTransferModalRowForm(sku, {
      serverError: `Unable to get product: ${err.error_message || err.message}`,
    })
  }
}

export function removeSKUFromCreateInventoryTransfer(sku) {
  const {skus} = createInventoryTransferModalFormSelector(getState())

  updateCreateInventoryTransferModalForm({
    skus: skus.filter((s) => s !== sku),
  })
}
